import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { useTenancy } from '@/contexts/TenancyContext';

interface PrivacyActState {
  enabled: "enabled" | "disabled";
}

const defaultPrivacyAct: PrivacyActState = {
  enabled: "disabled",
};

export default function PrivacyActModule() {
  const { activeTenancy } = useTenancy();
  const [privacyAct, setPrivacyAct] = useState<PrivacyActState>(defaultPrivacyAct);
  const [privacyActInitial, setPrivacyActInitial] = useState<PrivacyActState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [privacyManagerDetails, setPrivacyManagerDetails] = useState("");
  const [privacyOfficerDetails, setPrivacyOfficerDetails] = useState("");   

  useEffect(() => {
    const fetchPrivacyAct = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const privacyActSettings = settings?.["privacyAct"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof privacyActSettings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(privacyActSettings.Enabled);
        } else if (typeof privacyActSettings.enabled !== 'undefined') {
          isEnabled = checkEnabled(privacyActSettings.enabled);
        } else if (typeof privacyActSettings.active !== 'undefined') {
          isEnabled = checkEnabled(privacyActSettings.active);
        } else if (typeof privacyActSettings.Active !== 'undefined') {
          isEnabled = checkEnabled(privacyActSettings.Active);
        }
        setPrivacyAct({ enabled: isEnabled ? "enabled" : "disabled" });
        setPrivacyActInitial({ enabled: isEnabled ? "enabled" : "disabled" });
        setPrivacyManagerDetails(privacyActSettings.privacyManagerDetails || "");
        setPrivacyOfficerDetails(privacyActSettings.privacyOfficerDetails || "");
      } catch (err) {
        toast.error("Failed to fetch Privacy Act settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrivacyAct();
  }, []);

  const privacyActChanged = privacyActInitial && JSON.stringify(privacyAct) !== JSON.stringify(privacyActInitial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = privacyActInitial?.enabled === "disabled";
      const isNowEnabled = privacyAct.enabled === "enabled";
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        privacyAct: { Enabled: privacyAct.enabled === "enabled",
          privacyManagerDetails: privacyManagerDetails,
          privacyOfficerDetails: privacyOfficerDetails,
         },
      });
      
      // DED-1511: If Privacy Act is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying Privacy Act tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'PrivacyAct',
            undefined,
            undefined
          );
          
          toast.success("Privacy Act settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying Privacy Act tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("Privacy Act settings saved");
      }
      
      setPrivacyActInitial({ ...privacyAct });
    } catch (err) {
      toast.error("Failed to save Privacy Act settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="Privacy Act">🕵️‍♂️</span>
        </div>
        <div>
          <div className="font-semibold text-lg">Privacy Act</div>
          <div className="text-xs text-muted-foreground">Privacy Act Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={privacyAct.enabled}
          onValueChange={val => setPrivacyAct(v => ({ ...v, enabled: val as PrivacyActState["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Privacy Manager Details */}
      <div>
        <label className="block text-sm font-medium mb-1">Privacy Manager Details</label>
        <div className="flex gap-2">
         <Textarea
         placeholder="Privacy Manager Details"
         value={privacyManagerDetails}
         onChange={e => setPrivacyManagerDetails(e.target.value)}
         disabled={isLoading || isSaving}
         />
        </div>
      </div>
      {/* Privacy Officer Details */}
      <div>
        <label className="block text-sm font-medium mb-1">Privacy Officer Details</label>
        <div className="flex gap-2">
         <Textarea
         placeholder="Privacy Officer Details"
         value={privacyOfficerDetails}
         onChange={e => setPrivacyOfficerDetails(e.target.value)}
         disabled={isLoading || isSaving}
         />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!privacyActChanged || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save Privacy Act Settings"}
        </Button>
      </div>
    </div>
  );
}
